import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RestaurantWorkPeriod, NewRestaurantWorkPeriod } from '../../../interfaces/period';
import { BehaviorSubject } from 'rxjs';
import {
  dayEndValidator,
  dayStartValidator, endOffsetValidator, startOffsetValidator,
  timeEndValidator,
  timeStartValidator,
} from '../../../shared/custom-validators';
import { ConfirmationDialogComponent } from '../../modals/confirm-dialog/confirmation-dialog.component';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

type RestaurantScheduleForm = {
  startDay: FormControl<number | null>;
  startTime: FormControl<string | null>;
  devicesAutoOn: FormControl<boolean>;
  devicesAutoOnOffset: FormControl<number>;
  endDay: FormControl<number | null>;
  endTime: FormControl<string | null>;
  devicesAutoOff: FormControl<boolean>;
  devicesAutoOffOffset: FormControl<number>;
};

@UntilDestroy()
@Component({
  selector: 'app-restaurant-schedule',
  templateUrl: './restaurant-schedule.component.html',
  styleUrls: ['./restaurant-schedule.component.scss']
})
export class RestaurantScheduleComponent implements OnInit, OnChanges {

  @Input()
  public period!: RestaurantWorkPeriod;
  @Input()
  public periods!: RestaurantWorkPeriod[];
  @Input()
  public editable!: boolean;

  @Output()
  newPeriod = new EventEmitter<NewRestaurantWorkPeriod>();
  @Output()
  cancel = new EventEmitter<boolean>();
  @Output()
  delete = new EventEmitter<number>();
  @Output()
  update = new EventEmitter<RestaurantWorkPeriod>();

  public newPeriodForm!: FormGroup<RestaurantScheduleForm>;
  public weekdays: {name: string, index: number}[] = [];
  public isEdit = new BehaviorSubject<boolean>(false);
  public offsetOptions: { label: string; value: number }[] = [
    { label: '60 min. before', value: -3600 },
    { label: '50 min. before', value: -3000 },
    { label: '40 min. before', value: -2400 },
    { label: '30 min. before', value: -1800 },
    { label: '20 min. before', value: -1200 },
    { label: '10 min. before', value: -600 },
    { label: 'Without Offset', value: 0 },
    { label: '10 min', value: 600 },
    { label: '20 min', value: 1200 },
    { label: '30 min', value: 1800 },
    { label: '40 min', value: 2400 },
    { label: '50 min', value: 3000 },
    { label: '60 min', value: 3600 },
  ];

  constructor(
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.weekdays = [
      { index: 1, name: 'Monday' },
      { index: 2, name: 'Tuesday' },
      { index: 3, name: 'Wednesday' },
      { index: 4, name: 'Thursday' },
      { index: 5, name: 'Friday' },
      { index: 6, name: 'Saturday' },
      { index: 7, name: 'Sunday' }
    ];
    this.newPeriodForm = this.generateForm();
    this.newPeriodForm.patchValue(this.period);

    this.isEdit.pipe(
      untilDestroyed(this)
    ).subscribe((value) => {
      if (value && this.period) {
        this.newPeriodForm.enable();
      } else {
        this.newPeriodForm.disable();
      }
    });

    this.setupDateTimeFields();
  }

  private generateForm(): FormGroup<RestaurantScheduleForm> {
    return new FormGroup({
      startDay: new FormControl({value: this.period?.startDay ?? null, disabled: true}, [
        Validators.required,
        dayStartValidator(this.periods, this.period?.id ?? null)
      ]),
      startTime: new FormControl({value: this.period?.startTime ?? null, disabled: true},  [
        Validators.required,
        timeStartValidator(this.periods, this.period?.id ?? null)
      ]),
      devicesAutoOn: new FormControl<boolean>({value: this.period?.devicesAutoOn ?? false, disabled: true}, { nonNullable: true }),
      devicesAutoOnOffset: new FormControl<number>({value: this.period?.devicesAutoOnOffset ?? 0, disabled: true}, {
        nonNullable: true,
        validators: [
        startOffsetValidator(this.periods, this.period?.id ?? null)
      ]}),
      endDay: new FormControl({value: this.period?.endDay ?? null, disabled: true}, [
        Validators.required,
        dayEndValidator(this.periods, this.period?.id ?? null)
      ]),
      endTime: new FormControl({value: this.period?.endTime ?? null, disabled: true}, [
        Validators.required,
        timeEndValidator(this.periods, this.period?.id ?? null)
      ]),
      devicesAutoOff: new FormControl<boolean>({value: this.period?.devicesAutoOff ?? false, disabled: true}, { nonNullable: true }),
      devicesAutoOffOffset: new FormControl<number>({value: this.period?.devicesAutoOffOffset ?? 0, disabled: true}, {
        nonNullable: true,
        validators: [
        endOffsetValidator(this.periods, this.period?.id ?? null)
      ]}),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.periods) {
      this.updateValidators();
    }
  }

  private setupDateTimeFields(): void {
    const startDayControl = this.newPeriodForm.get('startDay');
    const startTimeControl = this.newPeriodForm.get('startTime');
    const onControl = this.newPeriodForm.get('devicesAutoOn');
    const onOffsetControl = this.newPeriodForm.get('devicesAutoOnOffset');
    const endDayControl = this.newPeriodForm.get('endDay');
    const endTimeControl = this.newPeriodForm.get('endTime');
    const offControl = this.newPeriodForm.get('devicesAutoOff');
    const offOffsetControl = this.newPeriodForm.get('devicesAutoOffOffset');

    if (!this.period) {
      startDayControl?.enable();
    }

    startDayControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (startDayControl?.valid && (this.isEdit.getValue() || !this.period)) {
        startTimeControl?.enable();
      }
    });

    startTimeControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (startTimeControl?.valid && (this.isEdit.getValue() || !this.period)) {
        endDayControl?.enable();
      }
    });

    endDayControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (endDayControl?.valid && (this.isEdit.getValue() || !this.period)) {
        endTimeControl?.enable();
      }
    });

    endTimeControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (endTimeControl?.valid && (this.isEdit.getValue() || !this.period)) {
        onControl?.enable();
        offControl?.enable();
      }
    });

    onControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((enabled: boolean) => {
      if (enabled && (this.isEdit.getValue() || !this.period)) {
        onOffsetControl?.enable();
      } else if (!enabled) {
        onOffsetControl?.disable();
      }
    });

    offControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((enabled: boolean) => {
      if (enabled && (this.isEdit.getValue() || !this.period)) {
        offOffsetControl?.enable();
      } else if (!enabled) {
        offOffsetControl?.disable();
      }
    });
  }

  public createPeriod(): void {
    this.newPeriod.emit(this.getPeriodValue());
    this.newPeriodForm.reset({
      startDay: null,
      startTime: null,
      devicesAutoOn: false,
      devicesAutoOnOffset: 0,
      endDay: null,
      endTime: null,
      devicesAutoOff: false,
      devicesAutoOffOffset: 0,
    });
    this.newPeriodForm.controls.startDay.enable({ emitEvent: false });
    this.newPeriodForm.controls.startTime.disable({ emitEvent: false });
    this.newPeriodForm.controls.devicesAutoOn.disable({ emitEvent: false });
    this.newPeriodForm.controls.devicesAutoOnOffset.disable({ emitEvent: false });
    this.newPeriodForm.controls.endDay.disable({ emitEvent: false });
    this.newPeriodForm.controls.endTime.disable({ emitEvent: false });
    this.newPeriodForm.controls.devicesAutoOff.disable({ emitEvent: false });
    this.newPeriodForm.controls.devicesAutoOffOffset.disable({ emitEvent: false });
  }

  public editPeriod(id: number): void {

    if (this.isEdit.getValue() && id === this.period.id) {
      const isFormChange = !this.areEqual(this.period, this.getPeriodValue());

      if (isFormChange) {
        const title = this.translate.instant('period.unsavedPeriodTitle');
        const message = this.translate.instant('period.unsavedChanges');
        const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
          data: {
            message,
            title
          },
          panelClass: 'confirmation-modal'
        });

        confirmationModal.afterClosed().pipe(
          filter(Boolean),
        ).subscribe((): void => {
          this.newPeriodForm.patchValue(this.period);
          this.isEdit.next(false);
        });
      } else {
        this.isEdit.next(false);
      }
    } else {
      this.isEdit.next(true);
    }
  }

  public deletePeriod(id: number): void {
    const title = this.translate.instant('period.workingTime.deletePeriodTitle');
    const message = this.translate.instant('period.workingTime.deletePeriod');

    const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.delete.emit(id);
    });
  }

  public updatePeriod(id: number): void {
    this.update.emit({id, ...this.getPeriodValue()});
    this.isEdit.next(false);
  }

  public cancelCreation(): void {
    this.cancel.emit(false);
  }

  private areEqual = (a: RestaurantWorkPeriod, b: NewRestaurantWorkPeriod): boolean => {
    return (
      a.startDay === b.startDay &&
      a.startTime === b.startTime &&
      a.endDay === b.endDay &&
      a.endTime === b.endTime &&
      a.devicesAutoOn === b.devicesAutoOn &&
      a.devicesAutoOnOffset === b.devicesAutoOnOffset &&
      a.devicesAutoOff === b.devicesAutoOff &&
      a.devicesAutoOffOffset === b.devicesAutoOffOffset
    );
  }

  private getPeriodValue(): NewRestaurantWorkPeriod {
    return this.newPeriodForm.getRawValue() as NewRestaurantWorkPeriod;
  }

  private updateValidators(): void {
    if (!this.newPeriodForm) {
      this.newPeriodForm = this.generateForm();
      return;
    }


    this.newPeriodForm.get('startDay')?.setValidators([
      Validators.required,
      dayStartValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('startTime')?.setValidators([
      Validators.required,
      timeStartValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('endDay')?.setValidators([
      Validators.required,
      dayEndValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('endTime')?.setValidators([
      Validators.required,
      timeEndValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('devicesAutoOnOffset')?.setValidators([
      startOffsetValidator(this.periods, this.period?.id ?? null)
    ]);

    this.newPeriodForm.get('devicesAutoOffOffset')?.setValidators([
      endOffsetValidator(this.periods, this.period?.id ?? null)
    ]);

    this.newPeriodForm.updateValueAndValidity();
  }
}
